import React, { useEffect, useState } from "react";
import { Loader2, Wallet } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import paymentService from "@/services/paymentService";
import orderService from "@/services/orderService";
import { formatCurrency } from "@/lib/format";

// Usada só se GET /payments falhar
const FALLBACK_METHODS = [
  "PIX",
  "Dinheiro",
  "Cartão de Crédito",
  "Cartão de Débito",
  "Vale Refeição",
];

/**
 * Confirma o recebimento do pedido (PUT /pix/{code}/received). O valor
 * sugerido é o total do pedido (itens − cupom + frete).
 */
const PaymentConfirmDialog = ({ open, onOpenChange, order, onConfirm, saving = false }) => {
  const [methods, setMethods] = useState(FALLBACK_METHODS);
  const [form, setForm] = useState({ pay_method: "", value_received: "", name: "", contact: "" });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    let active = true;
    paymentService
      .getNames()
      .then((names) => {
        if (active && names?.length) setMethods(names);
      })
      .catch(() => {
        // Modal global já exibido pelo interceptor; mantém a lista padrão
      });
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (open && order) {
      setForm({
        pay_method: order.pay_method || "",
        value_received: orderService.calculateTotal(order).toFixed(2),
        name: order.client?.name || "",
        contact: order.client?.contact || "",
      });
      setErrors({});
    }
  }, [open, order]);

  if (!order) return null;

  // O método salvo no pedido pode não estar na lista cadastrada
  const options =
    form.pay_method && !methods.includes(form.pay_method)
      ? [form.pay_method, ...methods]
      : methods;
  const total = orderService.calculateTotal(order);
  const received = Number(form.value_received) || 0;
  const change = received - total;

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!form.pay_method) nextErrors.pay_method = "Selecione a forma de pagamento";
    if (!(received > 0)) nextErrors.value_received = "Informe um valor maior que zero";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onConfirm({
      ...form,
      amount: received,
      value_received: received,
      orderCode: order.code,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !saving && onOpenChange(value)}>
      <DialogContent className="max-w-md p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2 font-display text-primary">
              <Wallet className="h-5 w-5" />
              Confirmar pagamento
            </DialogTitle>
            <DialogDescription>
              Pedido <strong>#{order.code}</strong>
              {order.client?.name ? ` · ${order.client.name}` : ""} · total{" "}
              <strong className="text-primary">{formatCurrency(total)}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="payment-method">
              Forma de pagamento <span className="text-destructive">*</span>
            </Label>
            <Select
              value={form.pay_method}
              onValueChange={(value) => setForm((prev) => ({ ...prev, pay_method: value }))}
            >
              <SelectTrigger id="payment-method" className="h-11">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {options.map((method) => (
                  <SelectItem key={method} value={method}>
                    {method}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.pay_method && <p className="text-xs text-destructive">{errors.pay_method}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="value-received">
              Valor recebido (R$) <span className="text-destructive">*</span>
            </Label>
            <Input
              id="value-received"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.value_received}
              onChange={(e) => setForm((prev) => ({ ...prev, value_received: e.target.value }))}
              className="h-11"
            />
            {errors.value_received ? (
              <p className="text-xs text-destructive">{errors.value_received}</p>
            ) : (
              received > 0 &&
              Math.abs(change) >= 0.01 && (
                <p className={change > 0 ? "text-xs text-primary" : "text-xs text-terra"}>
                  {change > 0
                    ? `Troco: ${formatCurrency(change)}`
                    : `Faltam ${formatCurrency(-change)} para o total`}
                </p>
              )
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="payment-name">Nome do cliente</Label>
              <Input
                id="payment-name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="payment-contact">Contato</Label>
              <Input
                id="payment-contact"
                value={form.contact}
                onChange={(e) => setForm((prev) => ({ ...prev, contact: e.target.value }))}
                className="h-11"
                placeholder="Telefone ou e-mail"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} className="hover:bg-brand-dark">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? "Confirmando..." : "Confirmar pagamento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentConfirmDialog;
